import { Investment } from '@/types';
import type { CollaboratorPermissionType, CollaboratorStatusType } from '@/types/database/generated/Collaborator';
import type {
    InvestmentFinancingType,
    InvestmentMarketType,
    InvestmentType,
    InvestmentVisibilityType,
} from '@/types/database/generated/Investment';
import type { MemberPermissionType, MemberStatusType } from '@/types/database/generated/Member';
import type { UserAuthenticationService, UserPlan } from '@/types/database/generated/User';

import { apiClient } from '../http';
import { favoriteService } from './favorites';
import { createResourceService, QueryParams } from './resources';

type InvestmentRelationInput = {
    id: string;
};

export type InvestmentMutationInput = {
    tickerSymbol?: string | null;
    name?: string | null;
    editedName?: string | null;
    description?: string | null;
    address?: string | null;
    country?: string | null;
    cik?: string | null;
    lei?: string | null;
    figi?: string | null;
    foundingDate?: string | null;
    ipoDate?: string | null;
    headquarters?: string | null;
    ceo?: string | null;
    employees?: number | null;
    exchange?: string | null;
    industry?: string | null;
    sector?: string | null;
    issuance?: string | null;
    website?: string | null;
    tags?: string[];
    financingType?: InvestmentFinancingType;
    marketType?: InvestmentMarketType;
    type?: InvestmentType;
    visibility?: InvestmentVisibilityType;
    isFavorite?: boolean;
    isLocked?: boolean;
    isArchived?: boolean;
    bannerImage?: string | null;
    clearbit?: Record<string, unknown> | null;
    folder?: InvestmentRelationInput | null;
    team?: InvestmentRelationInput | null;
    pipelineColumn?: InvestmentRelationInput | null;
};

const base = createResourceService<Investment, InvestmentMutationInput, InvestmentMutationInput>('/api/investments');

export const normalizeInvestmentEmployeeCount = (value: unknown): number | null | undefined => {
    if (value === undefined || value === null) {
        return value;
    }

    const employeeCount =
        typeof value === 'number'
            ? value
            : typeof value === 'string' && (/^\d+$/.test(value.trim()) || /^\d{1,3}(,\d{3})+$/.test(value.trim()))
              ? Number(value.trim().replaceAll(',', ''))
              : Number.NaN;

    return Number.isSafeInteger(employeeCount) && employeeCount >= 0 && employeeCount <= 2_147_483_647
        ? employeeCount
        : undefined;
};

const normalizeRelation = (value: unknown): InvestmentRelationInput | null | undefined => {
    if (value === undefined || value === null) {
        return value;
    }

    if (typeof value !== 'object' || Array.isArray(value) || !('id' in value) || typeof value.id !== 'string') {
        return undefined;
    }

    return { id: value.id };
};

const normalizeRecord = (value: unknown): Record<string, unknown> | null | undefined => {
    if (value === undefined || value === null) {
        return value;
    }

    return typeof value === 'object' && !Array.isArray(value) ? { ...value } : undefined;
};

export const toInvestmentMutationInput = (input: Partial<Investment>): InvestmentMutationInput => ({
    tickerSymbol: input.tickerSymbol,
    name: input.name,
    editedName: input.editedName,
    description: input.description,
    address: input.address,
    country: input.country,
    cik: input.cik,
    lei: input.lei,
    figi: input.figi,
    foundingDate: input.foundingDate,
    ipoDate: input.ipoDate,
    headquarters: input.headquarters,
    ceo: input.ceo,
    employees: normalizeInvestmentEmployeeCount(input.employees),
    exchange: input.exchange,
    industry: input.industry,
    sector: input.sector,
    issuance: input.issuance,
    website: input.website,
    tags: input.tags,
    financingType: input.financingType,
    marketType: input.marketType,
    type: input.type,
    visibility: input.visibility,
    isFavorite: input.isFavorite,
    isLocked: input.isLocked,
    isArchived: input.isArchived,
    bannerImage: input.bannerImage,
    clearbit: normalizeRecord(input.clearbit),
    folder: normalizeRelation(input.folder),
    team: normalizeRelation(input.team),
    pipelineColumn: normalizeRelation(input.pipelineColumn),
});

type EntityReference = string | { id: string };

export type InvestmentCollaboratorUser = {
    id: string;
    firstName?: string;
    lastName?: string;
    emailAddress?: string;
    username?: string | null;
    avatar?: string;
    plan?: UserPlan;
    authenticationService?: UserAuthenticationService;
};

export type InvestmentCollaboratorMember = {
    id: string;
    user?: InvestmentCollaboratorUser;
    permission?: MemberPermissionType;
    status?: MemberStatusType;
    createdAt?: Date | string;
    updatedAt?: Date | string;
};

export type InvestmentCollaborator = {
    id: string;
    investment?: EntityReference;
    member?: EntityReference | InvestmentCollaboratorMember;
    permission?: CollaboratorPermissionType;
    status?: CollaboratorStatusType;
    createdAt?: Date | string;
    updatedAt?: Date | string;
};

export type InvestmentCollaboratorInput = Partial<InvestmentCollaborator>;

export const investmentService = {
    ...base,
    create: (input: Partial<Investment>) => base.create(toInvestmentMutationInput(input)),
    createCollaborator: (investmentId: string, input: InvestmentCollaboratorInput) =>
        apiClient.post<InvestmentCollaborator, InvestmentCollaboratorInput>(
            `/api/investments/${investmentId}/collaborators`,
            input,
        ),
    deleteCollaborator: (investmentId: string, collaboratorId: string) =>
        apiClient.delete<void>(`/api/investments/${investmentId}/collaborators/${collaboratorId}`),
    getCollaborators: (investmentId: string) =>
        apiClient.get<InvestmentCollaborator[]>(`/api/investments/${investmentId}/collaborators`),
    list: <TParams extends QueryParams = QueryParams>(params?: TParams) => base.list(params),
    setArchived: (id: string, isArchived: boolean) => base.update(id, { isArchived }),
    setFavorite: (id: string, isFavorite: boolean) => favoriteService.set<Investment>('investments', id, isFavorite),
    setLocked: (id: string, isLocked: boolean) => base.update(id, { isLocked }),
    update: (id: string, updates: Partial<Investment>) => base.update(id, toInvestmentMutationInput(updates)),
    updateCollaborator: (investmentId: string, collaboratorId: string, updates: InvestmentCollaboratorInput) =>
        apiClient.put<InvestmentCollaborator, InvestmentCollaboratorInput>(
            `/api/investments/${investmentId}/collaborators/${collaboratorId}`,
            updates,
        ),
};
